import { LOCALE_BY_LANGUAGE } from '../../components/format-money';
import type { SupportedLanguage } from '../../i18n';

/** Formatters by language and number of decimals: building an `Intl.NumberFormat` is the costly part. */
const DECIMAL_FORMATS = new Map<string, Intl.NumberFormat>();

function decimalFormat(language: SupportedLanguage, digits: number): Intl.NumberFormat {
  const key = `${language}:${String(digits)}`;
  let format = DECIMAL_FORMATS.get(key);
  if (format === undefined) {
    format = new Intl.NumberFormat(LOCALE_BY_LANGUAGE[language], {
      useGrouping: false,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    DECIMAL_FORMATS.set(key, format);
  }
  return format;
}

function isDecimal(text: string): text is `${number}` {
  return /^\d+(?:\.\d+)?$/.test(text);
}

function fractionDigits(decimal: string): number {
  const dot = decimal.indexOf('.');
  return dot === -1 ? 0 : decimal.length - dot - 1;
}

/**
 * Formats a contract decimal string (price, lots, percent) in the UI language, keeping every digit
 * it was sent with and never going through a float (spec R10, C1 R6): `"3340.85"` → `"3340,85"`
 * in French. No thousands separator, as on mockup 7 ("3340,00 / 3340,85").
 *
 * @param minimumFractionDigits - Pads shorter values, e.g. 2 for lots ("0.1" → "0.10").
 * @throws {RangeError} when `decimal` is not a plain non-negative decimal.
 * @example formatDecimal('2650.80', 'fr') // "2650,80"
 */
export function formatDecimal(
  decimal: string,
  language: SupportedLanguage,
  minimumFractionDigits = 0,
): string {
  if (!isDecimal(decimal)) throw new RangeError(`Not a decimal: ${decimal}`);
  const digits = Math.max(fractionDigits(decimal), minimumFractionDigits);
  // Intl reads a numeric string as an exact decimal (Intl.NumberFormat v3, ES2023).
  return decimalFormat(language, digits).format(decimal);
}

/**
 * Whether two decimal strings hold the same value, whatever their trailing zeros
 * (`"2650.8"` and `"2650.80"`). Used for the "at BE" label (spec R3): no float comparison.
 */
export function isSameDecimal(a: string, b: string): boolean {
  return normalize(a) === normalize(b);
}

function normalize(decimal: string): string {
  return decimal.includes('.') ? decimal.replace(/\.?0+$/, '') : decimal;
}
